import {
  CompoundFilter,
  FormResponseValue,
  FormResponseAnswerNumber,
  FormResponseAnswerString,
  FormResponseAnswerAddress,
} from "@tellescope/types-models";
import { FORM_LOGIC_CALCULATED_FIELDS, FORM_LOGIC_URL_PARAMETER } from "./constants";
import { getAge } from "@tellescope/utils";
import { calculateFormScoring } from "./calculate-form-scoring";
import { evaluateResponseEquals } from "./evaluate-response-equals";
import { Indexable, NextFieldLogicOptions } from "./types";

/**
 * Evaluates whether responses satisfy given compound filter conditions
 * Supports $and, $or, and various comparison operators
 * Handles calculated fields like BMI, Age, Score, Gender, and State
 */
export const responsesSatisfyConditions = (
  responses: FormResponseValue[],
  conditions: CompoundFilter<string>,
  options?: NextFieldLogicOptions
): boolean => {
  const key = Object.keys(conditions)[0] as "$and" | "$or" | "condition" | "string";

  if (key === "$and") {
    const andConditions = conditions[key] as CompoundFilter<string>[];
    for (const c of andConditions) {
      if (!responsesSatisfyConditions(responses, c, options)) {
        return false;
      }
    }
    return true;
  } else if (key === "$or") {
    const orConditions = conditions[key] as CompoundFilter<string>[];
    for (const c of orConditions) {
      if (responsesSatisfyConditions(responses, c, options)) {
        return true;
      }
    }
    return false;
  } else if (key === "condition") {
    const fieldIdOrCalculated = Object.keys(conditions[key] as object)[0];
    const answer =
      fieldIdOrCalculated === FORM_LOGIC_CALCULATED_FIELDS[0] // bmi
        ? (() => {
            const h =
              responses.find((r) => r.answer.type === "number" && r.answer.value && r.computedValueKey === "Height")
                ?.answer ||
              responses.find((r) => r.answer.type === "Height" && r.answer.value && r.computedValueKey === "Height")
                ?.answer;
            const w = responses.find(
              (r) => r.answer.type === "number" && r.answer.value && r.computedValueKey === "Weight"
            )?.answer;

            const height =
              h?.type === "number" && h.value
                ? h.value
                : h?.type === "Height" && typeof h.value?.feet === "number"
                ? h.value.feet * 12 + (h.value.inches || 0)
                : undefined;
            const weight = w?.type === "number" && w.value ? w?.value : undefined;

            if (!(height && weight)) return undefined;

            const BMI: FormResponseAnswerNumber = {
              type: "number",
              value: (703 * weight) / (height * height),
            };

            return BMI;
          })()
        : fieldIdOrCalculated === FORM_LOGIC_CALCULATED_FIELDS[1] // age
        ? (() => {
            const dob =
              options?.dateOfBirth ||
              responses
                .find((r) => r.answer.type === "dateString" && r.answer.value && r.computedValueKey === "Date of Birth")
                ?.answer?.value?.toString();
            if (!dob) return undefined;

            try {
              const age = getAge(new Date(dob));
              if (typeof age !== "number") return undefined;

              const Age: FormResponseAnswerNumber = {
                type: "number",
                value: age,
              };

              return Age;
            } catch (err) {
              console.error(err);
            }
          })()
        : fieldIdOrCalculated === FORM_LOGIC_CALCULATED_FIELDS[2] // score
        ? (() => {
            const form = options?.form;
            if (!form) return undefined;

            const scores = calculateFormScoring({
              response: {
                responses: options.activeResponses || responses,
              },
              form,
            });
            if (!scores?.length) return;

            const Score: FormResponseAnswerNumber = {
              type: "number",
              value: scores[0]?.value,
            };

            return Score;
          })()
        : fieldIdOrCalculated === FORM_LOGIC_CALCULATED_FIELDS[3] // gender
        ? (() => {
            const gender =
              (
                responses.find(
                  (r) =>
                    (r.answer.type === "Dropdown" || r.answer.type === "multiple_choice") &&
                    r.computedValueKey === "Gender"
                )?.answer?.value as string[]
              )?.[0] || options?.gender;

            const Gender: FormResponseAnswerString = {
              type: "string",
              value: gender || "",
            };

            return Gender;
          })()
        : fieldIdOrCalculated === FORM_LOGIC_CALCULATED_FIELDS[4] // state
        ? (() => {
            const state =
              (
                responses.find((r) => r.answer.type === "Address" && r.computedValueKey === "State")
                  ?.answer as FormResponseAnswerAddress
              )?.value?.state || options?.state;

            const State: FormResponseAnswerString = {
              type: "string",
              value: state || "",
            };

            return State;
          })()
        : fieldIdOrCalculated === FORM_LOGIC_URL_PARAMETER
        ? ({
            type: "string",
            value: options?.urlLogicValue || "",
          } as FormResponseAnswerString)
        : responses.find((r) => r.fieldId === fieldIdOrCalculated)?.answer;
    if (!answer) return false;

    if (!fieldIdOrCalculated) return true;

    const comparison = (conditions[key] as Indexable)[fieldIdOrCalculated];
    if (typeof comparison === "string") {
      return evaluateResponseEquals(answer, comparison);
    } else {
      const condition = Object.keys(comparison)[0] as
        | "$ne"
        | "$exists"
        | "$contains"
        | "$doesNotContain"
        | "$range"
        | "$lt"
        | "$gt";
      const conditionValue = comparison[condition];

      if (condition === "$ne") {
        return !evaluateResponseEquals(answer, conditionValue);
      }

      if (condition === "$lt" || condition === "$gt") {
        if (conditionValue === "$now") {
          if (answer.type === "date" && answer.value) {
            const number = Date.now();
            const answerNumber = new Date(answer.value).getTime();
            if (condition === "$lt") {
              return answerNumber < number;
            }
            if (condition === "$gt") {
              return answerNumber > number;
            }
          }
          if (answer.type === "dateString" && answer.value) {
            const number = new Date().getTime();
            const answerNumber = new Date(answer.value).getTime();
            if (condition === "$lt") {
              return answerNumber < number;
            }
            if (condition === "$gt") {
              return answerNumber > number;
            }
          }
        }

        const number = parseInt(conditionValue);
        const answerNumber = answer.value as number;
        if (isNaN(number)) return false;
        if (typeof answerNumber !== "number") return false;

        if (condition === "$lt") {
          return answerNumber < number;
        }
        if (condition === "$gt") {
          return answerNumber > number;
        }
      } else if (condition === "$exists") {
        return (
          (answer.value !== undefined &&
            answer.value !== null &&
            answer.value !== "" &&
            !(Array.isArray(answer.value) && answer.value.length === 0)) === conditionValue
        );
      } else if (condition === "$contains" || condition === "$doesNotContain") {
        if (answer.value === undefined || answer.value === null || answer.value === "") {
          return condition === "$doesNotContain"; // empty responses cannot contain anything
        }
        if (answer.type === "Database Select" && answer.value?.length) {
          const contains = !!answer.value.find((v) => v?.text?.includes(conditionValue));
          return (contains && condition === "$contains") || (!contains && condition === "$doesNotContain");
        }
        if (Array.isArray(answer.value)) {
          const contains = (answer.value as string[]).find((v) => typeof v === "string" && v.includes(conditionValue));
          return (contains && condition === "$contains") || (!contains && condition === "$doesNotContain");
        }
        if (typeof answer.value === "string") {
          const contains = answer.value.includes(conditionValue);
          return (contains && condition === "$contains") || (!contains && condition === "$doesNotContain");
        }
      }
    }
  }

  return true;
};
